export {
  Logo,
  GovernedCandidateLogo,
  canRenderGovernedCandidateLogo,
  resolveCandidateLogoVariant,
  resolveLegacyLogoSize,
  resolveLogoSize,
  type GovernedCandidateLogoProps,
  type LogoProps,
  type LogoVariant,
  type ResolvedLogoSize,
  type ResolvedLegacyLogoSize,
} from './logo';
export {
  EXPERIENCE_RENDER_MODES,
  ExperienceModeBoundary,
  canActivateExperienceEvidenceBoundary,
  useExperienceModeBoundary,
  useExperienceRenderMode,
  type ExperienceModeBoundaryProps,
  type ExperienceRenderMode,
} from './experience-mode-boundary';
export {
  PROGRAM_CONTEXTS,
  ProgramContextIndicator,
  normaliseProgramContextDetail,
  type ProgramContext,
  type ProgramContextIndicatorProps,
} from './program-context-indicator';
export {
  ContentDensitySection,
  resolvePreHydrationContentDensityPreference,
  type ContentDensitySectionProps,
} from './content-density-section';
export {
  InteractiveCardCollection,
  type InteractiveCardCollectionItem,
  type InteractiveCardCollectionProps,
} from './interactive-card-collection';
export { Button, type ButtonProps } from './button';
export { buttonVariants } from './button-variants';
export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  cardVariants,
  type CardProps,
} from './card';
export { Badge, badgeVariants, type BadgeProps } from './badge';
export { CountBadge, type CountBadgeProps } from './count-badge';
export { Tag, type TagProps } from './tag';
export { EmptyValue, type EmptyValueProps } from './empty-value';
export { Input, nativeFieldClassName, type InputProps } from './input';
export { PasswordField, type PasswordFieldProps } from './password-field';
export {
  REMEMBERED_EMAIL_STORAGE_KEY,
  normalizeRememberedEmail,
  persistRememberedEmailValue,
  useRememberedEmail,
} from './use-remembered-email';
export { Label } from './label';
export { FieldLabel, type FieldLabelProps } from './field-label';
export { Form, type FormProps } from './form';
export { Table, type TableProps } from './table';
export { StatPill, type StatPillProps } from './stat-pill';
export { Avatar, type AvatarProps } from './avatar';
export { LoadingSkeleton, type LoadingSkeletonProps } from './loading-skeleton';
export { ErrorState, type ErrorStateProps } from './error-state';
export { EmptyState, type EmptyStateProps } from './empty-state';
export {
  DashboardPageHeader,
  DashboardLoadingState,
  DashboardErrorState,
  DashboardEmptyState,
  type DashboardPageHeaderProps,
  type DashboardLoadingStateProps,
  type DashboardErrorStateProps,
  type DashboardEmptyStateProps,
} from './dashboard-page-state';
export * from './telehealth';

// Phase 3 — Design system components
export {
  DataTable,
  BulkActionBar,
  type DataTableProps,
  type DataTableServerPagination,
  type DataTableColumn,
  type BulkAction,
  type BulkActionBarProps,
  type SortState,
  type SortDirection,
} from './data-table';
export {
  DataGrid,
  DataGridToolbar,
  DataGridFilterControls,
  DataGridColumnControls,
  DataGridSavedViews,
  DENSITY_ROW_CLASSES,
  buildCsv,
  // `downloadGridCsv` (not `downloadCsv`) — the package root already
  // exports a differently-shaped `downloadCsv` from `./utils/download`.
  // Codex Phase A pass 1 caught the silent override.
  downloadGridCsv,
  type DataGridProps,
  type DataGridServerPagination,
  type DataGridToolbarMode,
  type DataGridToolbarProps,
  type DataGridFilterControlsProps,
  type DataGridColumnControlsProps,
  type DataGridSavedViewsProps,
  type SavedView,
  DENSITY_ROW_HEIGHT_PX,
  type DataGridDensity,
  type DataGridColumn,
  type DataGridBulkAction,
  type DataGridEmptyState,
  type DataGridSelection,
  type FilterSpec,
  type ActiveFilters,
} from './data-grid';
export {
  DetailPage,
  DetailPageHeader,
  type DetailPageProps,
  type DetailPageHeaderProps,
} from './detail-page';
export {
  SubPageTabs,
  resolveActiveTab,
  type SubPageTabsProps,
  type SubPageTab,
} from './sub-page-tabs';
export { SectionTabsProvider, SectionTabsSlot } from './sub-page-tabs/sub-page-tabs';
export { PermissionDenied, type PermissionDeniedProps } from './permission-denied';
export {
  EXPERIENCE_STATE_NAMES,
  ExperienceState,
  type ExperienceStateName,
  type ExperienceStateProps,
} from './experience-state';
export {
  Field,
  type FieldProps,
  type FieldControlProps,
  FormShell,
  type FormShellProps,
  type FormShellSection,
  type FormShellErrorSummaryItem,
} from './form-shell';
export { Wizard, type WizardProps, type WizardStep } from './wizard';
export { V2Table, type V2TableProps } from './v2-table';
export {
  ExperienceRecordEvidence,
  type ExperienceRecordEvidenceProps,
} from './experience-record-evidence';
export { DatePicker, type DatePickerProps } from './date-picker';
export { ConfirmDialog, type ConfirmDialogProps } from './confirm-dialog';
export { PrintView, type PrintViewProps } from './print-view';
export { SkipToContent, type SkipToContentProps } from './skip-to-content';
export {
  DateEntryInput,
  formatPartialAuDateInput,
  parseIsoDateInput,
  formatIsoDateForInput,
  isoDateRangeError,
  expandTwoDigitYear,
  isUnambiguousAuDateText,
  dateEntryValidationMessage,
  canCommitLiveParse,
  addDaysIso,
  addMonthsIso,
  buildMonthGrid,
  type DateEntryInputProps,
} from './date-entry-input';
export {
  TimeEntryInput,
  HH_MM_RE,
  formatPartialTimeInput,
  parseTimeInput,
  timeEntryValidationMessage,
  type TimeEntryInputProps,
} from './time-entry-input';
export {
  TimeRangePicker,
  resolveTimeRangeToIso,
  type TimeRangePickerProps,
  type TimeRange,
  type TimeRangePreset,
} from './time-range-picker';
export {
  DashboardRefreshControl,
  type DashboardRefreshControlProps,
} from './dashboard-refresh-control';
export {
  PeriodComparison,
  resolveComparisonRange,
  comparisonDelta,
  type PeriodComparisonProps,
  type ComparisonMode,
} from './period-comparison';
export { DashboardPdfButton, type DashboardPdfButtonProps } from './dashboard-pdf-button';
export {
  ActivityTimeline,
  type ActivityTimelineProps,
  type ActivityTimelineEvent,
  type ActivityTimelineFilter,
} from './activity-timeline';
export { Dialog, DialogFooter, type DialogProps } from './dialog';
export { useModalLayer } from './use-modal-layer';
export { AlertDialog, type AlertDialogProps } from './alert-dialog';
export { Sheet, type SheetProps } from './sheet';
export { Popover, type PopoverProps } from './popover';
export { Tooltip, type TooltipProps } from './tooltip';
export { Collapsible, type CollapsibleProps } from './collapsible';
export { Select, type SelectProps, type SelectOption } from './select';
export {
  SearchableSelect,
  type SearchableSelectProps,
  type SearchableSelectOption,
} from './searchable-select';
export { Textarea, type TextareaProps } from './textarea';
export { Tabs, type TabsProps, type Tab } from './tabs';
export { DropdownMenu, type DropdownMenuProps, type DropdownMenuItem } from './dropdown-menu';
export { Breadcrumb, type BreadcrumbProps, type BreadcrumbItem } from './breadcrumb';
export { MetricCard, type MetricCardProps, type MetricTrend } from './metric-card';
export { StatusBadge, type StatusBadgeProps, type StatusType } from './status-badge';
export { Timeline, type TimelineProps, type TimelineItem } from './timeline';
export { FileUpload, type FileUploadProps } from './file-upload';
export {
  DisplayTitle,
  PageTitle,
  SectionTitle,
  Eyebrow,
  BodyText,
  CaptionText,
  MetricText,
} from './typography';

// Action-oriented dashboard components
export {
  AlertPanel,
  type AlertPanelProps,
  type AlertItem,
  type AlertSeverity,
} from './alert-panel';
export {
  SummaryCard,
  SummaryCardGrid,
  type SummaryCardProps,
  type SummaryCardTrend,
} from './summary-card';
export { FocusedList, type FocusedListProps, type FocusedListTab } from './focused-list';

// Customisation components
export { CustomiseButton, type CustomiseButtonProps } from './customise-button';
export {
  CustomisePanel,
  type CustomisePanelProps,
  type CustomisePanelItem,
} from './customise-panel';
export {
  ColumnVisibilityDropdown,
  type ColumnVisibilityDropdownProps,
  type ColumnVisibilityItem,
} from './column-visibility-dropdown';

export { MoreControls } from './more-controls';

// Screen composition primitives
export { PageHeader, type PageHeaderProps, type PageHeaderAction } from './page-header';
export { SectionCard, type SectionCardProps } from './section-card';
export { KeyFacts, type KeyFactsProps, type KeyFact, type KeyFactGroup } from './key-facts';
export { AboutThisPage, type AboutThisPageProps } from './about-this-page';
export { Stepper, type StepperProps, type StepperStep, type StepperState } from './stepper';
export { ChecklistList, type ChecklistListProps, type ChecklistItem } from './checklist-list';
export {
  CompositionDensityProvider,
  useCompositionDensity,
  type CompositionDensity,
} from './composition-density';
export { Section, type SectionProps } from './section';
export {
  RegisterToolbar,
  type RegisterToolbarProps,
  type RegisterFilter,
  type RegisterExportAction,
} from './register-toolbar';
export {
  RegisterDateRange,
  type RegisterDateRangeProps,
  type RegisterDateRangeValue,
} from './register-date-range';
export {
  AttentionQueue,
  type AttentionQueueProps,
  type AttentionQueueItem,
} from './attention-queue';
export { WorkList, type WorkListProps, type WorkListRow, type WorkListField } from './work-list';
export {
  compareAttention,
  groupAttentionItems,
  attentionCountLabel,
  type AttentionItem,
  type AttentionSeverity,
} from './attention-model';
export {
  CountedAttentionQueue,
  CountedAttentionRow,
  LayoutPendingContext,
  type CountedAttentionChrome,
} from './counted-attention-queue';
export { WorkHomePage, type WorkHomePageProps } from './work-home-page';
export { CollapsibleSection, type CollapsibleSectionProps } from './collapsible-section';
export {
  CustomisePage,
  isPageItemVisible,
  type CustomisePageProps,
  type CustomisePageItem,
} from './customise-page';
export { FormSection, type FormSectionProps } from './form-section';
export { FormActions, type FormActionsProps } from './form-actions';
export {
  FieldStatic,
  fieldHintId,
  fieldErrorId,
  type FieldStaticProps,
} from './form-shell/field-static';
export { JourneyActions, type JourneyActionsProps } from './journey-actions';
export { TechnicalValue, type TechnicalValueProps } from './technical-value';
export { StateKit, type StateKitProps } from './state-kit';
export { RecordWorkspace, type RecordWorkspaceProps } from './record-workspace';

export { PageContainer, type PageContainerProps } from './page-container';

export { NextActionRow } from './next-action-row';
export { DateNav, ExceptionsTrigger, formatDateRange } from './date-nav';
export { Progress, type ProgressProps } from './progress';
export { FormActionBar, type FormActionBarProps } from './form-action-bar';
export { AttentionRow } from './attention-row';
export { NumbersStrip, StatCell } from './numbers-strip';

export { NativeSelect } from './native-select';
export { SettingsLayout, ContextRail } from './settings-layout';
export { Banner, BannerSlot, InfoCard } from './detail-page/banner';
export { controlSizeClassName, type ControlSize, type ControlDensity } from './control-styles';
export { SegmentedControl, type SegmentedControlProps } from './segmented-control';

export { RecordOverview, type RecordOverviewProps } from './record-overview';

export { Checkbox, type CheckboxProps } from './checkbox';
